"use client";
import { useTranslations } from "next-intl";
import { useState, useRef, useEffect } from "react";
import { useRouter } from "@/navigation";

type Props = {
	className?: string;
	lastScrollPosition?: number;
};

export default function Search({ className, lastScrollPosition }: Props) {
	const t = useTranslations();
	const router = useRouter();
	const [inputValue, setInputValue] = useState("");
	const [isExpanded, setIsExpanded] = useState(false);
	const [isLoading, setIsLoading] = useState(false);
	const inputRef = useRef<HTMLInputElement>(null);
	const containerRef = useRef<HTMLDivElement>(null);

	useEffect(() => {
		const handleClickOutside = (event: MouseEvent) => {
			if (
				containerRef.current &&
				!containerRef.current.contains(event.target as Node) &&
				isExpanded &&
				!inputValue
			) {
				setIsExpanded(false);
			}
		};

		document.addEventListener("mousedown", handleClickOutside);
		return () => {
			document.removeEventListener("mousedown", handleClickOutside);
		};
	}, [isExpanded, inputValue]);

	const handleSearch = async () => {
		if (!inputValue.trim()) return;

		try {
			setIsLoading(true);
			await router.push(`/search?query=${encodeURIComponent(inputValue.trim())}`);
		} finally {
			setIsLoading(false);
		}
	};

	const handleInputChange = (event: React.ChangeEvent<HTMLInputElement>) => {
		setInputValue(event.target.value);
	};

	const handleSearchClick = () => {
		if (isExpanded && inputValue.trim()) {
			handleSearch();
		} else {
			setIsExpanded(!isExpanded);
			if (!isExpanded) {
				setTimeout(() => {
					inputRef.current?.focus();
				}, 100);
			}
		}
	};

	const handleKeyDown = (event: React.KeyboardEvent) => {
		if (event.key === "Enter") {
			handleSearch();
		} else if (event.key === "Escape") {
			setIsExpanded(false);
			inputRef.current?.blur();
		}
	};

	const clearInput = () => {
		setInputValue("");
		inputRef.current?.focus();
	};

	return (
		<div ref={containerRef} className={`relative flex h-8 items-center ${className}`}>
			<div
				className={`flex items-center overflow-hidden transition-all duration-300 ease-out ${
					isExpanded ? "w-[220px] rounded-full bg-brand-ink/4" : "w-8 rounded-full bg-transparent"
				}`}
			>
				<input
					ref={inputRef}
					value={inputValue}
					onChange={handleInputChange}
					onKeyDown={handleKeyDown}
					type="text"
					className={`font-body h-8 w-full bg-transparent pl-3.5 pr-16 text-[13px] text-brand-ink outline-none transition-opacity duration-300 ${
						isExpanded ? "opacity-100" : "opacity-0"
					}`}
				/>
				<div className="absolute right-0 top-1/2 flex -translate-y-1/2 items-center">
					{isExpanded && inputValue && (
						<button
							onClick={clearInput}
							className="flex h-7 w-7 items-center justify-center text-brand-ink/35 hover:text-brand-ink/60"
							title={t("nav.clear")}
						>
							<i className="ri-close-line text-[15px]" />
						</button>
					)}
					<button
						onClick={handleSearchClick}
						disabled={isLoading}
						className={`flex h-8 w-8 items-center justify-center rounded-full transition-colors duration-200 ${
							isLoading ? "cursor-wait opacity-50" : ""
						} ${isExpanded ? "" : "hover:bg-brand-ink/5"}`}
					>
						{isLoading ? (
							<i className="ri-loader-4-line animate-spin text-[17px] text-brand-ink/50" />
						) : (
							<i
								className={`ri-search-line text-[18px] text-brand-ink/45 transition-colors duration-200 hover:text-brand-ink ${
									isExpanded && inputValue ? "!text-brand-fg" : ""
								}`}
							/>
						)}
					</button>
				</div>
			</div>
		</div>
	);
}
